import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { Clock, Play, Square, Calendar, User, Search, CheckCircle2, AlertCircle, Trash2, CheckSquare, Square as SquareIcon, ChevronLeft, ChevronRight } from 'lucide-react';

export default function Attendance() {
  const { user } = useContext(AuthContext);
  const [activeEntry, setActiveEntry] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  const loadData = async () => {
    setLoading(true);
    try {
      const t = Date.now();
      const [statusRes, logsRes] = await Promise.all([
        api.get(`/attendance/status?_t=${t}`),
        isAdmin ? api.get(`/attendance/logs?_t=${t}`) : Promise.resolve({ data: [] })
      ]);
      setActiveEntry(statusRes.data.activeEntry);
      if (isAdmin) setLogs(logsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    loadData(); 
    const handleSync = () => loadData();
    window.addEventListener('attendance-changed', handleSync);
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => {
      clearInterval(timer);
      window.removeEventListener('attendance-changed', handleSync);
    };
  }, [isAdmin]);

  const handleClockIn = async () => {
    setActionLoading(true);
    try {
      await api.post('/attendance/clock-in');
      window.dispatchEvent(new Event('attendance-changed'));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to clock in');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClockOut = async () => {
    setActionLoading(true);
    try {
      await api.post('/attendance/clock-out');
      window.dispatchEvent(new Event('attendance-changed'));
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to clock out');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDuration = (start, end) => {
    if (!start) return '0h 0m';
    const diff = (end ? new Date(end) : currentTime) - new Date(start);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  const filteredLogs = logs.filter(log => 
    (log.user?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredLogs.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentLogs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentLogs.map(l => l.id)));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this log?')) return;
    try {
      await api.delete(`/attendance/${id}`);
      setLogs(prev => prev.filter(l => l.id !== id));
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting log');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} logs?`)) return;
    try {
      await api.post('/attendance/bulk-delete', { ids: Array.from(selectedIds) });
      setLogs(prev => prev.filter(l => !selectedIds.has(l.id)));
      setSelectedIds(new Set());
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting logs');
    }
  };

  return (
    <div className="space-y-8 animate-fade-in-up pb-10 max-w-6xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight flex items-center gap-3">
            <Clock size={32} className="text-brand" /> Time & Attendance
          </h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-xs">Manage shifts and track hours.</p>
        </div>
      </div>

      <div className="bg-theme-card p-8 rounded-[2.5rem] border border-theme shadow-2xl flex flex-col md:flex-row items-center justify-between gap-8 bg-gradient-to-br from-theme-card to-theme-main/5 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-brand/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4"></div>
        
        <div className="flex items-center gap-6 z-10">
          <div className="w-24 h-24 rounded-full bg-theme-main border-2 border-theme flex items-center justify-center shadow-inner">
            <span className="text-3xl font-black text-brand uppercase tracking-tighter">
              {(user?.name || 'U')[0]}
            </span>
          </div>
          <div>
            <h3 className="text-2xl font-black text-theme-main uppercase tracking-tight">{user?.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={`w-2.5 h-2.5 rounded-full ${activeEntry ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}></span>
              <span className="text-xs font-black uppercase tracking-widest text-theme-muted">
                {activeEntry ? 'Currently Clocked In' : 'Currently Clocked Out'}
              </span>
            </div>
            {activeEntry && (
              <p className="text-sm font-bold text-brand mt-2 font-mono bg-brand/10 px-3 py-1 rounded-lg inline-block">
                Duration: {formatDuration(activeEntry.clockIn, null)}
              </p>
            )}
          </div>
        </div>

        <div className="z-10 w-full md:w-auto flex justify-center">
          {activeEntry ? (
            <button 
              onClick={handleClockOut}
              disabled={actionLoading}
              className="w-full md:w-64 py-5 bg-rose-500 hover:bg-rose-600 text-white rounded-3xl font-black text-sm uppercase tracking-widest shadow-xl shadow-rose-500/20 transition-all active:scale-95 flex flex-col items-center gap-2 disabled:opacity-50"
            >
              <Square size={24} />
              Clock Out
            </button>
          ) : (
            <button 
              onClick={handleClockIn}
              disabled={actionLoading}
              className="w-full md:w-64 py-5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-3xl font-black text-sm uppercase tracking-widest shadow-xl shadow-emerald-500/20 transition-all active:scale-95 flex flex-col items-center gap-2 disabled:opacity-50"
            >
              <Play size={24} />
              Clock In
            </button>
          )}
        </div>
      </div>

      {isAdmin && (
        <div className="space-y-6 pt-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <h3 className="text-lg font-black text-theme-main uppercase tracking-widest flex items-center gap-2">
              <Calendar size={20} className="text-brand" /> Attendance Logs
            </h3>
            <div className="flex gap-3">
              {selectedIds.size > 0 && (
                <button 
                  onClick={handleBulkDelete}
                  className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-2.5 rounded-xl shadow-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 animate-bounce-in"
                >
                  <Trash2 size={16} /> Delete ({selectedIds.size})
                </button>
              )}
              <div className="relative w-64">
                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-theme-muted" />
                <input 
                  type="text" 
                  placeholder="Search staff..." 
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  className="w-full bg-theme-main border border-theme rounded-xl py-2.5 pl-10 pr-4 text-theme-main text-xs font-bold outline-none focus:ring-2 focus:ring-brand transition-all"
                />
              </div>
            </div>
          </div>

          <div className="bg-theme-card rounded-[2rem] border border-theme shadow-soft overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-theme-main/10 border-b border-theme text-[10px] font-black uppercase tracking-widest text-theme-muted">
                    <th className="pl-8 py-5 w-10">
                      <button onClick={toggleSelectAll} className="text-brand">
                        {selectedIds.size === currentLogs.length && currentLogs.length > 0 ? <CheckSquare size={18} /> : <SquareIcon size={18} />}
                      </button>
                    </th>
                    <th className="px-4 py-5">Employee</th>
                    <th className="px-6 py-5">Date</th>
                    <th className="px-6 py-5">Clock In</th>
                    <th className="px-6 py-5">Clock Out</th>
                    <th className="px-6 py-5 text-right">Duration</th>
                    <th className="px-8 py-5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme">
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="px-8 py-10 text-center text-theme-muted text-sm font-bold">Loading logs...</td>
                    </tr>
                  ) : currentLogs.map(log => (
                    <tr key={log.id} className={`hover:bg-theme-main/5 transition-colors group ${selectedIds.has(log.id) ? 'bg-brand/5' : ''}`}>
                      <td className="pl-8 py-5">
                        <button onClick={() => toggleSelect(log.id)} className={selectedIds.has(log.id) ? 'text-brand' : 'text-theme-muted opacity-30 hover:opacity-100'}>
                          {selectedIds.has(log.id) ? <CheckSquare size={18} /> : <SquareIcon size={18} />}
                        </button>
                      </td>
                      <td className="px-4 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-theme-main text-theme-muted flex items-center justify-center font-black text-[10px] uppercase border border-theme">
                            {(log.user?.name || 'S')[0]}
                          </div>
                          <div>
                            <p className="text-sm font-bold text-theme-main">{log.user?.name}</p>
                            <p className="text-[10px] font-black uppercase tracking-widest text-brand">{log.user?.role}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="text-xs font-bold text-theme-main">{new Date(log.clockIn).toLocaleDateString()}</span>
                      </td>
                      <td className="px-6 py-5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-500 text-xs font-black uppercase border border-emerald-500/20">
                          {new Date(log.clockIn).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        {log.clockOut ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-rose-500/10 text-rose-500 text-xs font-black uppercase border border-rose-500/20">
                            {new Date(log.clockOut).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                          </span>
                        ) : (
                          <span className="text-[10px] font-black uppercase text-amber-500 tracking-widest animate-pulse flex items-center gap-1">
                            <AlertCircle size={12} /> Active Shift
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-right">
                        <span className="font-mono text-sm font-black text-theme-main">
                          {formatDuration(log.clockIn, log.clockOut)}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <button 
                          onClick={() => handleDelete(log.id)}
                          className="p-2 bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white rounded-xl transition-all shadow-sm active:scale-95"
                          title="Delete Log"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!loading && filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan="7" className="px-8 py-16 text-center">
                        <Calendar size={48} className="text-theme-muted opacity-20 mx-auto mb-4" />
                        <h3 className="text-xl font-black text-theme-main uppercase tracking-tight mb-1">No Logs Found</h3>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {totalPages > 1 && (
              <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
                  Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> logs
                </p>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20 hover:border-brand transition-all"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  
                  {(() => {
                    const pages = [];
                    let start = Math.max(1, currentPage - 1);
                    let end = Math.min(totalPages, start + 2);
                    if (end - start < 2) start = Math.max(1, end - 2);
                    for (let i = start; i <= end; i++) pages.push(i);
                    return pages.map(p => (
                      <button
                        key={p}
                        onClick={() => paginate(p)}
                        className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                          currentPage === p 
                            ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                            : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                        }`}
                      >
                        {p}
                      </button>
                    ));
                  })()}

                  <button 
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20 hover:border-brand transition-all"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
